import { useCallback, useMemo, useState } from "react";
import { foldUmlauts } from "../utils/dashboardFormat.js";

export const TASK_TABS = [
  { key: "alle", label: "Alle offenen" },
  { key: "neu", label: "Neu eingegangen" },
  { key: "ueberfaellig", label: "Überfällig" },
  { key: "extern", label: "Wartet extern" },
];

const TAB_PREDICATES = {
  alle: () => true,
  neu: (task) => task.is_new,
  ueberfaellig: (task) => task.overdue,
  extern: (task) => task.waiting_on !== "makler",
};

/**
 * Filter state for the "Meine Aufgaben" board: a tab, an optional age bucket and
 * a free-text query, all applied client-side over the already-loaded rows.
 *
 * The reset rules are asymmetric, matching the click dummy — they live here
 * rather than in scattered setState calls precisely because they are the kind of
 * thing that silently drifts:
 *
 *   - picking a tab              keeps the age filter
 *   - picking an age bucket      resets the tab to "alle" (and toggles off when
 *                                the same bucket is picked again)
 *   - picking a waiting group    resets the age filter and jumps to alle/extern
 *   - typing in the search box   touches neither
 *
 * Counts for the pills come from the server aggregates, never from the filtered
 * array — otherwise the numbers would shrink as you filter.
 */
export function useDashboardTaskFilters(tasks = []) {
  const [tab, setTabState] = useState("alle");
  const [age, setAge] = useState(null);
  const [query, setQuery] = useState("");

  const setTab = useCallback((next) => setTabState(next), []);

  const toggleAge = useCallback((key) => {
    setAge((current) => (current === key ? null : key));
    setTabState("alle");
  }, []);

  const clearAge = useCallback(() => setAge(null), []);

  /**
   * "Bei uns in Bearbeitung" has no dedicated tab, so it falls back to `alle`;
   * the two external groups both map to `extern`. Lossy, but it matches the
   * dummy and keeps the tab row to four options.
   */
  const selectWaiting = useCallback((key) => {
    setTabState(key === "makler" ? "alle" : "extern");
    setAge(null);
  }, []);

  // Precompute the search haystack once per data change. Built here rather than
  // shipped by the API so "what is searchable" stays a UI decision.
  const haystacks = useMemo(
    () =>
      tasks.map((task) =>
        foldUmlauts(
          [task.title, task.sub, task.code, task.waiting_on_label].filter(Boolean).join(" ")
        )
      ),
    [tasks]
  );

  const filtered = useMemo(() => {
    const predicate = TAB_PREDICATES[tab] ?? TAB_PREDICATES.alle;
    const needle = foldUmlauts(query.trim());

    return tasks.filter((task, i) => {
      if (!predicate(task)) return false;
      if (age && task.age_bucket !== age) return false;
      if (needle && !haystacks[i].includes(needle)) return false;
      return true;
    });
  }, [tasks, tab, age, query, haystacks]);

  const isFiltered = tab !== "alle" || age != null || query.trim() !== "";

  return {
    tab,
    age,
    query,
    filtered,
    isFiltered,
    setTab,
    toggleAge,
    clearAge,
    selectWaiting,
    setQuery,
  };
}

export default useDashboardTaskFilters;
